import { DateRangePicker, Hint, Tooltip, getDate } from '@systemeio/ui'
import { actionTitleLocoKeys } from 'modules/automation-rules/actions/constants/action-loco-keys'
import { useEffect } from 'react'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { withErrorBoundary } from 'shared/hocs/with-error-boundary'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { OrderEnum } from 'shared/types/pagination-interface'
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format'
import { getDateRangeSinceDate, getToday } from 'shared/utils/get-dates'
import { ContactId, ContactInterface } from '../../types/contact-interface'
import { useContactAutomationTasks } from '../hooks/use-contact-automation-tasks'
import { ContactAutomationTaskInterface } from '../types/contact-automation-task-interface'
import { ContactPageFilterInterface } from '../types/contact-page-filter-interface'
import ContactAutomationTaskLink from './contact-automation-task-link'

interface ContactAutomationTasksProps {
  contactId: ContactId
  contact?: ContactInterface
}

const defaultFilters: ContactPageFilterInterface = { date: [null, null] }

const ContactAutomationTasks = ({ contactId, contact }: ContactAutomationTasksProps) => {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()
  const { limit, setLimit, pagination, setPagination, filter, setFilter } = useFilterPagination(
    defaultFilters,
    true,
  )
  const { contactAutomationTasks } = useContactAutomationTasks({
    limit,
    pagination,
    contactId,
    filter,
  })

  useEffect(() => {
    if (contact) {
      setFilter({ date: getDateRangeSinceDate(contact.createdAt, user?.timezone) })
    }
  }, [contact, setFilter, user?.timezone])

  const columns: ColumnDefinitionInterface<ContactAutomationTaskInterface>[] = [
    {
      key: 'automationTask',
      header: t('dashboard.contact_page.automation_tasks.headers.name'),
      widthPercent: 60,
      onRender: ({ action, automationRule }) => (
        <div className="flex flex-col">
          <ContactAutomationTaskLink id={automationRule.id}>
            {t(
              action?.type
                ? actionTitleLocoKeys[action.type]
                : 'dashboard.contact_page.automation_tasks.view_task',
            )}
          </ContactAutomationTaskLink>
          {action && <span className="whitespace-normal">{action.data.name}</span>}
        </div>
      ),
    },
    {
      key: 'automationTask',
      header: t('dashboard.contact_page.automation_tasks.headers.date'),
      widthPercent: 40,
      onRender: ({ automationTask }) =>
        automationTask.completedAt && (
          <Tooltip
            wrapperClassName={'w-full'}
            mode={'hover'}
            label={dateTimeFormat(automationTask.completedAt, {
              timeFormat: user?.timeFormat,
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          >
            <span className={'w-full truncate'}>
              {dateFormat(automationTask.completedAt, {
                dateFormat: user?.dateFormat,
                timeZone: user?.timezone,
              })}
            </span>
          </Tooltip>
        ),
      onRenderSmall: ({ automationTask }) =>
        automationTask.completedAt && (
          <div className={'flex items-center gap-3'}>
            <span className={'text-text-primary font-bold'}>{t('global.date')}</span>
            <span>
              {dateTimeFormat(automationTask.completedAt, {
                timeFormat: user?.timeFormat,
                dateFormat: user?.dateFormat,
                timeZone: user?.timezone,
              })}
            </span>
          </div>
        ),
    },
  ]

  return (
    <ListingLayout
      type="compact"
      data={contactAutomationTasks}
      counter={false}
      titleClassName={'truncate'}
      title={
        <Hint
          className={'truncate'}
          label={t('dashboard.contact_page.automation_tasks.description')}
        >
          <h2 className={'h-[42px] truncate text-[17px] font-bold leading-[42px]'}>
            {t('dashboard.contact_page.automation_tasks.title_plural')}
          </h2>
        </Hint>
      }
      customHeaderElement={
        <DateRangePicker
          maxDate={getToday(user?.timezone)}
          minDate={getDate(contact?.createdAt, user?.timezone)}
          getDefaultDate={() => getDateRangeSinceDate(contact?.createdAt, user?.timezone)}
          date={filter.date}
          onChange={date => {
            setPagination({ startFromId: undefined, order: OrderEnum.next })
            setLimit(10)
            setFilter({ date: date })
          }}
          timezone={user?.timezone}
          createdAt={user?.createdAt}
          intlLocale={intlLocale}
        />
      }
    >
      <Table
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        data={contactAutomationTasks}
        columns={columns}
        nestedId="automationTask"
      />
    </ListingLayout>
  )
}

export default withErrorBoundary(ContactAutomationTasks)
